import React, { useState, useEffect } from "react";
import Template from "../../components/Template";
import { useNavigate } from "react-router-dom";
import FormLabel from "../../components/FormLabel";
import config from "../../config";
import ConfirmationModal from "../../components/ConfirmationModal";
import SuccessModal from "../../components/SuccessModal";
import api from "../../services/api";
import { maskPhone, maskCep, maskCpfCnpj } from "../../utils/masks";
import { formatDateWithoutHour } from "../../utils/date";
import usePermission from "../../hooks/usePermission";

const CadastrarFornecedor = ({ fornecedorId }) => {
    const navigate = useNavigate();
    const isEditMode = Boolean(fornecedorId);
    const podeDeletar = usePermission("DELETAR_FORNECEDOR");

    // Estado único do formulário
    const [formData, setFormData] = useState({
        id: "",                // Exemplo: campo "ID"
        nomeFornecedor: "",
        cpfCnpj: "",
        tipoFornecedor: "",
        razaoSocial: "",
        categoria: "",
        telefoneContato: "",
        email: "",
        urlContato: "",
        endereco: "",
        bairro: "",
        cep: "",
        municipio: "",
        uf: "",
        projetoVinculado: "",
        tipoDocumento: "",
        arquivoDocumento: "",
        statusFornecedor: "",
        qualificacao: "",
        observacoes: ``,
        tipoCadastro: '',
    });

    // Anexos escolhidos em "Anexos e Documentos Complementares"
    const [anexosSelecionados, setAnexosSelecionados] = useState([]);
    const [anexosRemovidos, setAnexosRemovidos] = useState([]);
    const [tiposDocumentos, setTiposDocumentos] = useState([]);
    const [statusList, setStatusList] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [obras, setObras] = useState([]);
    const [qualificacoes, setQualificacoes] = useState([]);
    const [anexoFile, setAnexoFile] = useState(null);

    const [showDialog, setShowDialog] = useState(false);
    const [selectedPdf, setSelectedPdf] = useState("");
    const [listAnexoFile, setListAnexoFile] = useState([]);

    const [showConfirmDeleteRegister, setShowConfirmDeleteRegister] = useState(false);
    const [showConfirmRemoveArchiveDialog, setShowConfirmRemoveArchiveDialog] = useState(false);

    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);

    // Busca o endereço pelo CEP usando ViaCEP
    const fetchAddressByCep = async () => {
        if (!formData.cep || formData.cep.length < 8) {
            alert("Insira um CEP válido.");
            return;
        }

        try {
            const response = await fetch(`https://viacep.com.br/ws/${formData.cep}/json/`);
            const data = await response.json();

            if (data.erro) {
                alert("CEP não encontrado.");
                return;
            }

            // Atualiza os campos de endereço, município e estado
            setFormData((prev) => ({
                ...prev,
                endereco: data.logradouro || "",
                municipio: data.localidade || "",
                bairro: data.bairro || "",
                uf: data.uf || "",
            }));
        } catch (error) {
            console.error("Erro ao buscar o endereço:", error);
            alert("Não foi possível buscar o endereço. Tente novamente.");
        }
    };

    // Para o anexoFile de "Anexos e Documentos Complementares"
    const handleAnexoFileChange = (e) => {
        setAnexoFile(e.target.files[0]);
    };

    const handleAddAnexo = () => {
        if (!formData.tipoDocumento) {
            alert("Selecione um Tipo de Documento antes de enviar.");
            return;
        }
        if (!anexoFile) {
            alert("Selecione um arquivo antes de enviar.");
            return;
        }

        // Encontra no array "tiposDocumentos" o objeto do tipo selecionado
        const tipoId = parseInt(formData.tipoDocumento, 10);
        const tipoEncontrado = tiposDocumentos.find((t) => t.id === tipoId);
        if (!tipoEncontrado) {
            alert("Tipo de documento não encontrado.");
            return;
        }

        // Cria objeto com tipo, file, data de envio, etc.
        const novoAnexo = {
            id: Date.now(), // ID local, único
            tipo: tipoId,
            tipoNome: tipoEncontrado.nome,
            file: anexoFile,
            dataEnvio: new Date().toISOString(), // ou formate como quiser
            isNew: true,
        };
        setAnexosSelecionados((prev) => [...prev, novoAnexo]);
        setListAnexoFile((prev) => [...prev, anexoFile])



        // Opcional: Resetar "Pesquisar Documento"
        setAnexoFile(null);
    };

    const handleRemoveAnexo = (anexoId) => {
        setAnexosSelecionados((prevSelecionados) => {
            const item = prevSelecionados.find((a) => a.id === anexoId);
            if (!item) return prevSelecionados; // se não achar, não faz nada

            if (item.isNew || String(item.id).startsWith('temp-')) {
                return prevSelecionados.filter((a) => a.id !== anexoId);
            } else {
                setAnexosRemovidos((prevRemovidos) => {
                    const jaExiste = prevRemovidos.some((r) => r.id === item.id);
                    if (!jaExiste) {
                        return [...prevRemovidos, item];
                    }
                    return prevRemovidos;
                });

                return prevSelecionados.filter((a) => a.id !== anexoId);
            }
        });
    };

    const handleShowPdf = (url) => {
        setSelectedPdf(url);
        setShowDialog(true);
    };

    const handleCloseDialog = () => {
        setShowDialog(false);
    };

    const handleCancel = () => {
        setShowConfirmDialog(false);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleDeleteFile = async () => {
        try {
            await api.delete(`/fornecedores/${fornecedorId}/arquivo`);
            setFormData((prev) => ({ ...prev, arquivoUrl: "" }));
            setShowConfirmRemoveArchiveDialog(false);
            setShowSuccessDialog(true);
        } catch (error) {
            console.error("Erro ao remover arquivo:", error);
            alert("Erro ao remover arquivo.");
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        setShowConfirmDialog(true);
    };

    const handleDeleteRegister = async () => {
        try {
            await api.delete(`/fornecedores/${fornecedorId}`);
            setShowConfirmDeleteRegister(false);
            navigate("/fornecedores");
        } catch (error) {
            console.error("Erro ao remover fornecedor:", error);
            alert("Erro ao remover fornecedor.");
        }
    };

    // Handler específico para campo de telefone
    const handlePhoneChange = (e) => {
        const originalValue = e.target.value;
        const masked = maskPhone(originalValue);
        setFormData((prev) => ({ ...prev, telefoneContato: masked }));
    };

    const handleCpfCnpjChange = (e) => {
        const value = e.target.value;
        // Aplica a máscara
        const maskedValue = maskCpfCnpj(value);
        setFormData((prev) => ({
            ...prev,
            cpfCnpj: maskedValue,
        }));
    };

    // Handler específico para CEP
    const handleCepChange = (e) => {
        const value = e.target.value;
        // Aplica a máscara
        const maskedValue = maskCep(value);
        setFormData((prev) => ({
            ...prev,
            cep: maskedValue,
        }));
    };

    useEffect(() => {
        // Carregar categorias e status da API
        const fetchData = async () => {
            try {
                const [tiposDocumentosRes, statusRes, categoriasRes, obrasRes, qualificacoesRes] = await Promise.all([
                    api.get("/tiposdocumentos"),
                    api.get("/statuses/byModule/nome/fornecedores"),
                    api.get("/categoriasfornecedor"),
                    api.get("/obras/listar/todas"),
                    api.get("/qualificacoes"),


                ]);
                setTiposDocumentos(tiposDocumentosRes.data);
                setStatusList(statusRes.data);
                setCategorias(categoriasRes.data);
                setObras(obrasRes.data);
                setQualificacoes(qualificacoesRes.data);
            } catch (error) {
                console.error("Erro ao carregar dados:", error);
            }
        };
        fetchData();
        if (isEditMode) {
            const fetchEdital = async () => {
                try {
                    const response = await api.get(`/fornecedores/${fornecedorId}`);
                    const data = response.data;

                    setFormData({
                        id: data.id,
                        nomeFornecedor: data.nomeFornecedor || "",
                        cpfCnpj: data.cpfCnpj || "",
                        tipoFornecedor: data.tipoFornecedor || "",
                        razaoSocial: data.razaoSocial || "",
                        categoria: data.categoria?.id || "",
                        telefoneContato: data.telefoneContato || "",
                        email: data.email || "",
                        urlContato: data.urlContato || "",
                        bairro: data.bairro || "",
                        cep: data.cep || "",
                        endereco: data.endereco || "",
                        municipio: data.municipio || "",
                        uf: data.uf || "",
                        statusFornecedor: data.status?.id || "",
                        observacoes: data.observacoes || "",
                        projetoVinculado: data.obra?.id || "",
                        qualificacao: data.qualificacao?.id || "",
                        tipoCadastro: data.tipo || ""
                    });
                    setAnexosSelecionados(data.anexos || []);
                    setAnexosRemovidos([]);


                } catch (error) {
                    console.error("Erro ao carregar fornecedor:", error);
                    alert("Erro ao carregar o fornecedor. Tente novamente.");
                }
            };
            fetchEdital();
        }
    }, [isEditMode, fornecedorId]);

    const handleConfirm = async (e) => {
        setShowConfirmDialog(false);

        try {

            // Montamos um FormData apenas se você quiser enviar "multipart/form-data"
            const dataToSend = new FormData();

            dataToSend.append("nomeFornecedor", formData.nomeFornecedor);
            dataToSend.append("cpfCnpj", formData.cpfCnpj);
            dataToSend.append("tipoFornecedor", formData.tipoFornecedor);
            dataToSend.append("razaoSocial", formData.razaoSocial);

            dataToSend.append("categoria", formData.categoria);
            dataToSend.append("telefoneContato", formData.telefoneContato);
            dataToSend.append("email", formData.email);
            dataToSend.append("urlContato", formData.urlContato);
            dataToSend.append("endereco", formData.endereco);

            dataToSend.append("cep", formData.cep);
            dataToSend.append("bairro", formData.bairro);
            dataToSend.append("municipio", formData.municipio);
            dataToSend.append("uf", formData.uf);

            dataToSend.append("projetoVinculado", formData.projetoVinculado);
            dataToSend.append("qualificacao", formData.qualificacao);

            dataToSend.append("observacoes", formData.observacoes);
            dataToSend.append("status", formData.statusFornecedor);
            dataToSend.append("anexosAdicionados", JSON.stringify(anexosSelecionados));
            dataToSend.append("anexosRemovidos", JSON.stringify(anexosRemovidos));

            dataToSend.append("tipoCadastro", formData.tipoCadastro);

            anexosSelecionados.forEach((anexo, index) => {
                dataToSend.append(`anexosFiles[${index}]`, anexo.file);
            });


            if (isEditMode) {
                await api.put(`/fornecedores/${fornecedorId}`, dataToSend, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
            } else {
                await api.post(`/fornecedores`, dataToSend, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
            }

            setShowSuccessDialog(true);
            setTimeout(() => {
                setShowSuccessDialog(false);
                if (isEditMode) {
                    window.location.href = `/fornecedores/editar/${fornecedorId}`;
                } else {
                    window.location.href = `/fornecedores`;
                }
            }, 3000);
        } catch (error) {
            console.error(`Erro ao ${isEditMode ? "atualizar" : "cadastrar"} fornecedor:`, error);
            alert("Ocorreu um erro. Verifique os dados e tente novamente.");
        }
    };

    // Botão Voltar
    const handleBack = () => {
        // Ajuste a rota conforme sua necessidade
        navigate("/fornecedores");
    };

    return (
        <Template>
            <form onSubmit={handleSubmit} className="w-full px-4 py-6 flex flex-col gap-8">
                {/* Título da Página */}
                <div>
                    <h1 className="text-2xl text-stone-800 font-medium">
                        {isEditMode ? "Editar Fornecedor/ Prestador" : "Cadastrar Fornecedor/ Prestador"}
                    </h1>
                </div>

                {/* Informações Básicas */}
                <div className="flex flex-col gap-4">
                    <div className="text-stone-600 text-base font-medium font-['Inter']">
                        Informações Básicas
                    </div>

                    {/* Linha 1 */}
                    <div className="flex flex-wrap gap-4">
                        {/* ID */}
                        <div className="w-[185px] flex flex-col gap-1.5">
                            <label className="text-slate-700 text-sm font-medium font-['Inter'] leading-tight">
                                ID
                            </label>
                            <input
                                type="text"
                                className="pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300"
                                value={formData.id}
                                readOnly
                            />
                        </div>

                        {/* Nome do Fornecedor */}
                        <div className="w-[300px] grow flex flex-col gap-1.5">
                            <FormLabel htmlFor={'nomeFornecedor'}
                                isRequired={true}
                            >
                                Nome do Fornecedor/ Prestador
                            </FormLabel>
                            <input
                                name="nomeFornecedor"
                                type="text"
                                className="pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300"
                                value={formData.nomeFornecedor}
                                onChange={handleInputChange}
                                required
                            />
                        </div>

                        {/* CPF / CNPJ */}
                        <div className="w-[374.75px] flex flex-col gap-1.5">
                            <FormLabel htmlFor={'cpfCnpj'}
                                isRequired={true}
                            >
                                CPF / CNPJ
                            </FormLabel>
                            <input
                                name="cpfCnpj"
                                type="text"
                                className="pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300"
                                value={formData.cpfCnpj}
                                onChange={handleCpfCnpjChange}
                                required
                            />
                        </div>
                    </div>

                    {/* Linha 2 */}
                    <div className="flex flex-wrap gap-4">
                        {/* Tipo de Fornecedor */}
                        <div className="w-[374.75px] flex flex-col gap-1.5">
                            <label className="text-slate-700 text-sm font-medium font-['Inter'] leading-tight">
                                Tipo de Fornecedor/ Prestador
                            </label>
                            <select
                                name="tipoFornecedor"
                                className="px-3 py-2 bg-white rounded-md border border-slate-300"
                                value={formData.tipoFornecedor}
                                onChange={handleInputChange}
                            >
                                <option value="">Selecionar</option>
                                <option>Pessoa Jurídica</option>
                                <option>Pessoa Física</option>
                            </select>
                        </div>

                        {/* Razão Social */}
                        <div className="grow flex flex-col gap-1.5">
                            <label className="text-slate-700 text-sm font-medium font-['Inter'] leading-tight">
                                Razão Social
                            </label>
                            <input
                                name="razaoSocial"
                                type="text"
                                className="pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300"
                                value={formData.razaoSocial}
                                onChange={handleInputChange}
                            />
                        </div>

                        {/* Categoria */}
                        <div className="w-[374.75px] flex flex-col gap-1.5">
                            <label className="text-slate-700 text-sm font-medium font-['Inter'] leading-tight">
                                Categoria
                            </label>
                            <select
                                name="categoria"
                                className="px-3 py-2 bg-white rounded-md border border-slate-300"
                                value={formData.categoria}
                                onChange={handleInputChange}
                            >
                                <option value="">Selecionar</option>
                                {categorias.map((objeto) => (
                                    <option key={objeto.id} value={objeto.id}>
                                        {objeto.nome}
                                    </option>
                                ))}

                            </select>
                        </div>
                    </div>

                    {/* Linha 3 */}
                    <div className="flex flex-wrap gap-4">
                        {/* Telefone de Contato */}
                        <div className="w-[250px] flex flex-col gap-1.5">
                            <label className="text-slate-700 text-sm font-medium font-['Inter'] leading-tight">
                                Telefone de Contato
                            </label>
                            <input
                                name="telefoneContato"
                                type="text"
                                className="pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300"
                                value={formData.telefoneContato}
                                onChange={handlePhoneChange}
                            />
                        </div>

                        {/* E-mail */}
                        <div className="w-[300px] grow flex flex-col gap-1.5">
                            <label className="text-slate-700 text-sm font-medium font-['Inter'] leading-tight">
                                E-mail
                            </label>
                            <input
                                name="email"
                                type="email"
                                className="pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300"
                                value={formData.email}
                                onChange={handleInputChange}
                            />
                        </div>

                        {/* URL de Contato */}
                        <div className="grow flex flex-col gap-1.5">
                            <label className="text-slate-700 text-sm font-medium font-['Inter'] leading-tight">
                                URL de Contato
                            </label>
                            <input
                                name="urlContato"
                                type="text"
                                className="pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300"
                                value={formData.urlContato}
                                onChange={handleInputChange}
                            />
                        </div>
                    </div>
                </div>

                {/* Endereço */}
                <div className="flex flex-col gap-4">
                    <div className="text-stone-600 text-base font-medium font-['Inter']">Endereço</div>
                    <div className="flex flex-wrap gap-4">
                        {/* CEP */}
                        <div className="w-[150px] flex flex-col gap-1.5">
                            <label className="text-slate-700 text-sm font-medium font-['Inter'] leading-tight">
                                CEP
                            </label>
                            <input
                                name="cep"
                                type="text"
                                className="pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300"
                                value={formData.cep || ""}
                                onChange={handleCepChange}
                                onBlur={fetchAddressByCep}
                            />
                        </div>
                        {/* Endereço */}
                        <div className="grow flex flex-col gap-1.5">
                            <label className="text-slate-700 text-sm font-medium font-['Inter'] leading-tight">
                                Endereço
                            </label>
                            <input
                                name="endereco"
                                type="text"
                                className="pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300"
                                value={formData.endereco}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div className="flex-1 flex flex-col gap-1.5">
                            <label className="text-slate-700 text-sm font-medium">Bairro</label>
                            <div className="bg-white border border-slate-300 rounded-md px-3 py-2">
                                <input
                                    type="text"
                                    name="bairro"
                                    className="text-slate-700 text-base outline-none w-full"
                                    value={formData.bairro}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>
                        {/* Município */}
                        <div className="grow flex flex-col gap-1.5">
                            <label className="text-slate-700 text-sm font-medium font-['Inter'] leading-tight">
                                Município
                            </label>
                            <input
                                name="municipio"
                                type="text"
                                className="pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300"
                                value={formData.municipio}
                                onChange={handleInputChange}
                            />
                        </div>

                        {/* UF */}
                        <div className="w-[108px] flex flex-col gap-1.5">
                            <label className="text-slate-700 text-sm font-medium font-['Inter'] leading-tight">
                                UF
                            </label>
                            <select
                                name="uf"
                                className="pl-3 pr-10 py-2 bg-white rounded-md border border-slate-300"
                                value={formData.uf}
                                onChange={handleInputChange}
                            >
                                <option value="">Selecione</option>
                                <option value="AC">AC</option>
                                <option value="AL">AL</option>
                                <option value="AP">AP</option>
                                <option value="AM">AM</option>
                                <option value="BA">BA</option>
                                <option value="CE">CE</option>
                                <option value="DF">DF</option>
                                <option value="ES">ES</option>
                                <option value="GO">GO</option>
                                <option value="MA">MA</option>
                                <option value="MT">MT</option>
                                <option value="MS">MS</option>
                                <option value="MG">MG</option>
                                <option value="PA">PA</option>
                                <option value="PB">PB</option>
                                <option value="PR">PR</option>
                                <option value="PE">PE</option>
                                <option value="PI">PI</option>
                                <option value="RJ">RJ</option>
                                <option value="RN">RN</option>
                                <option value="RS">RS</option>
                                <option value="RO">RO</option>
                                <option value="RR">RR</option>
                                <option value="SC">SC</option>
                                <option value="SP">SP</option>
                                <option value="SE">SE</option>
                                <option value="TO">TO</option>
                            </select>
                        </div>


                    </div>
                </div>

                {/* Anexos e Documentos Complementares (com scroll horizontal) */}
                <div className="flex flex-col gap-4">
                    <div className="text-stone-600 text-base font-medium font-['Inter']">
                        Anexos e Documentos Complementares
                    </div>
                    {/* Linha de Envio de Documento */}
                    <div className="flex flex-col md:flex-row flex-wrap justify-start items-start gap-4">
                        <div className="md:w-[375.75px] w-full flex-col justify-center gap-1.5 inline-flex">
                            <label className="text-slate-700 text-sm font-normal font-['Inter'] leading-tight">
                                Tipo de Documento
                            </label>
                            <div className="px-3 py-2 bg-white rounded-md border border-slate-300 inline-flex items-center gap-2.5">
                                <select
                                    name="tipoDocumento"
                                    value={formData.tipoDocumento}
                                    onChange={handleInputChange}
                                    className="grow bg-transparent outline-none text-slate-700 text-base"
                                >
                                    <option value="">Selecionar</option>
                                    {tiposDocumentos.map((objeto) => (
                                        <option key={objeto.id} value={objeto.id}>
                                            {objeto.nome}
                                        </option>
                                    ))}
                                </select>
                                <div className="w-4 h-4 px-1 justify-center items-center flex overflow-hidden" />
                            </div>
                        </div>
                        <div className="grow flex-col gap-1.5 inline-flex">
                            <label className="text-slate-700 text-sm font-normal font-['Inter'] leading-tight">
                                Selecionar Documento
                            </label>
                            <div className="flex gap-2">
                                <div className="relative flex items-center gap-4 w-full">
                                    <input
                                        name="arquivo"
                                        type="file"
                                        accept=".pdf"
                                        onChange={handleAnexoFileChange}
                                        className="absolute inset-0 opacity-0 cursor-pointer grow pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300 text-slate-400 text-base"

                                    />
                                    <div className={`w-full pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300 flex items-center gap-2`} >
                                        <span className="text-base text-slate-400">
                                            {anexoFile ? anexoFile.name : "Pesquisar Documento"}
                                        </span>
                                    </div>
                                </div>
                                <button type="button" className="px-4 py-2 bg-stone-600 rounded-md flex justify-center items-center gap-2.5" onClick={handleAddAnexo}>
                                    <div className="text-white text-sm font-normal font-['Inter'] leading-normal">
                                        Enviar
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>
                    {/* Tabela de Anexos (conteúdo fixo) */}
                    <div className="w-full overflow-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr>
                                    <th className="px-4 py-2 bg-stone-200 border border-slate-200 text-slate-800 text-base font-bold">
                                        Nº
                                    </th>
                                    <th className="px-4 py-2 bg-stone-200 border border-slate-200 text-slate-800 text-base font-bold">
                                        Tipo
                                    </th>
                                    <th className="px-4 py-2 bg-stone-200 border border-slate-200 text-slate-800 text-base font-bold">
                                        Data do Envio
                                    </th>
                                    <th className="px-4 py-2 bg-stone-200 border border-slate-200 text-slate-800 text-base font-bold">
                                        Remover
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {anexosSelecionados.length > 0 ? (
                                    anexosSelecionados.map((anexo) => (
                                        <tr key={anexo.id}>
                                            <td className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-base">{anexo.id}</td>
                                            <td className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-base">{anexo?.tipoNome ?? anexo?.tipo?.nome}</td>
                                            <td className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-base">{formatDateWithoutHour(anexo.dataEnvio)}</td>
                                            <td className="px-4 py-2 bg-white border border-slate-200 text-center">
                                                {(isEditMode && anexo.id && anexo.id.toString().length < 4) && (
                                                    <button type="button" onClick={() => handleShowPdf(anexo.arquivo)} title="Ver anexo">
                                                        <img className="w-10 h-10 px-[3px] py-0.5 flex-col justify-center items-center inline-flex overflow-hidden" src="/images/icon/search.png" />
                                                    </button>
                                                )}
                                                <button type="button" onClick={() => handleRemoveAnexo(anexo.id)} title="Remover anexo">
                                                    <img className="w-6 h-6 px-[3px] py-0.5 flex-col justify-center items-center inline-flex overflow-hidden" src="/images/icon/trash.png" />
                                                </button>

                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td
                                            colSpan={5}
                                            className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-base text-center"
                                        >                                                Nenhum anexo enviado.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    {/* NOVO CAMPO: Tipo de Cadastro (Fornecedor/Prestador) */}
                    <div className="flex flex-wrap gap-4">
                        <div className="w-[374.75px] flex flex-col gap-1.5">
                            <label className="text-slate-700 text-sm font-medium font-['Inter'] leading-tight">
                                Tipo de Cadastro
                            </label>
                            <select
                                name="tipoCadastro"
                                className="px-3 py-2 bg-white rounded-md border border-slate-300"
                                value={formData.tipoCadastro}
                                onChange={handleInputChange}
                            >
                                <option value="">Selecionar</option>
                                <option value="Fornecedor">Fornecedor</option>
                                <option value="Prestador">Prestador</option>
                                <option value="Fornecedor/Prestador">Fornecedor/Prestador</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Status e Observações + Atualizações */}
                <div className="flex flex-col md:flex-row gap-8">
                    {/* Status e Observações */}
                    <div className="grow flex flex-col gap-4">
                        <div className="text-stone-600 text-base font-medium font-['Inter']">
                            Status e Observações
                        </div>

                        {/* Status do Fornecedor */}
                        <div className="flex flex-col gap-1.5">
                            <FormLabel htmlFor="statusFornecedor" isRequired={true}>
                                Status do Fornecedor
                            </FormLabel>
                            <select
                                name="statusFornecedor"
                                className="px-3 py-2 bg-white rounded-md border border-slate-300"
                                value={formData.statusFornecedor}
                                onChange={handleInputChange}
                                required
                            >
                                <option value="">Selecionar</option>
                                {statusList.map((objeto) => (
                                    <option key={objeto.id} value={objeto.id}>
                                        {objeto.nome}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <FormLabel htmlFor="qualificacao" isRequired={false}>
                                Qualificação
                            </FormLabel>
                            <select
                                name="qualificacao"
                                className="px-3 py-2 bg-white rounded-md border border-slate-300"
                                value={formData.qualificacao}
                                onChange={handleInputChange}
                            >
                                <option value="">Selecionar</option>
                                {qualificacoes.map((objeto) => (
                                    <option key={objeto.id} value={objeto.id}>
                                        {objeto.nome}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Observações */}
                        <div className="flex flex-col gap-1.5">
                            <label className="text-[#1e1e1e] text-sm font-normal">
                                Observações
                            </label>
                            <textarea
                                name="observacoes"
                                rows={4}
                                className="w-full px-3 py-2 bg-white rounded-md border border-slate-300"
                                value={formData.observacoes}
                                onChange={handleInputChange}
                            />
                        </div>
                    </div>

                    {/* Atualizações (exemplo sem inputs)
                    <div className="flex flex-col gap-4">
                        <div className="text-black text-base font-medium">Atualizações</div>
                        <div className="text-slate-700 text-sm font-normal leading-tight">
                            ID: 1021 - João Silva atualizou documentos exigidos em 15/02/2024 14:30
                            <br />
                            ID: 1022 - Maria Fernandes alterou data de abertura em 25/03/2024 09:15
                            <br />
                            ID: 1023 - Pedro Almeida adicionou anexo do edital em 10/05/2024 16:45
                        </div>
                    </div>*/}
                </div>

                {/* Botões de Ação */}
                <div className="flex items-center w-full gap-4">
                    {isEditMode && podeDeletar && (
                        <button
                            type="button"
                            className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md flex items-center gap-2"
                            onClick={() => setShowConfirmDeleteRegister(true)}
                        >
                            <img className="w-4 h-4" src="/images/icon/trash-2.png" alt="Apagar" />
                            <span>Apagar</span>
                        </button>
                    )}

                    <div className="flex gap-4 ml-auto">
                        <button
                            type="button"
                            className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md flex items-center gap-2"
                            onClick={handleBack}
                        >
                            <img className="w-4 h-4" src="/images/icon/arrow-left.png" alt="Voltar" />
                            <span>Voltar</span>
                        </button>

                        <button
                            type="submit"
                            className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md flex items-center gap-2"
                        >
                            <img className="w-4 h-4" src="/images/icon/save.png" alt="Salvar" />
                            <span>Salvar</span>
                        </button>
                    </div>
                </div>


                {/** MODAIS de Confirmação / Sucesso */}
                <ConfirmationModal
                    isVisible={showConfirmDialog}
                    onConfirm={handleConfirm}
                    onCancel={handleCancel}
                    message="Deseja salvar as informações?"
                />

                <SuccessModal
                    isVisible={showSuccessDialog}
                    message="Informações salvas com sucesso!"
                    onClose={() => setShowSuccessDialog(false)}
                />

                <ConfirmationModal
                    isVisible={showConfirmRemoveArchiveDialog}
                    onConfirm={handleDeleteFile}
                    onCancel={() => setShowConfirmRemoveArchiveDialog(false)}
                    message="Deseja remover o arquivo deste edital?"
                    textButtonConfirm="Sim"
                />

                <ConfirmationModal
                    isVisible={showConfirmDeleteRegister}
                    onConfirm={handleDeleteRegister}
                    onCancel={() => setShowConfirmDeleteRegister(false)}
                    message="Deseja apagar este registro?"
                    textButtonConfirm="Sim"
                />
                {showDialog && (
                    <div className="fixed top-0 left-0 w-full h-full bg-black/90 z-50 p-10 flex-col justify-start items-end gap-10 inline-flex overflow-hidden">
                        <div className=" rounded-lg w-full max-w-[80%] h-full max-h-[90%] flex flex-col">
                            <div className="flex justify-between items-center p-4">
                                <div className="flex items-center gap-4">
                                    <a href={config.urlArquivo(selectedPdf)} download target="_blank">
                                        <img className="w-6 h-6 relative  overflow-hidden" src="/images/icon/download.png" />
                                    </a>
                                    <div className="w-6 h-6 relative  overflow-hidden" />
                                    <div className="w-6 h-6 relative  overflow-hidden" />
                                </div>
                                <img className="w-6 h-6 relative  overflow-hidden cursor-pointer" src="/images/icon/x.png" onClick={handleCloseDialog} />
                            </div>
                            <div className="flex-grow flex justify-center items-center p-4 overflow-hidden">
                                <iframe
                                    src={`${config.urlArquivo(selectedPdf)}#toolbar=0`}
                                    className="w-full h-full border-0"
                                    style={{ border: 'none' }}
                                    title="PDF Viewer"
                                />
                            </div>
                        </div>
                    </div>
                )}
            </form>
        </Template>
    );
};

export default CadastrarFornecedor;
